/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import { FormControl } from "baseui/form-control";
import { Input } from "baseui/input";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { DexterModal } from "../../Shared/DexterModal";
import { isNodeTitleValid } from "../TransformNodes/Util/TransformEngineDataUtils";

export interface NewMappingModalProps {
  isOpen: boolean;
  /** Called with the trimmed, validated name. */
  onCreate: (name: string) => void;
  /** Skip naming; the new mapping keeps its default name. */
  onCancel: () => void;
}

/** Asks for the name of a freshly created (empty) mapping. */
export const NewMappingModal: React.FC<NewMappingModalProps> = ({
  isOpen,
  onCreate,
  onCancel,
}) => {
  const { currentTheme } = useDexterTheme();
  const { colors, fonts, fontSizes } = currentTheme;

  const [name, setName] = React.useState("");
  const [touched, setTouched] = React.useState(false);

  // Every opening starts blank.
  React.useEffect(() => {
    if (isOpen) {
      setName("");
      setTouched(false);
    }
  }, [isOpen]);

  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && isNodeTitleValid(trimmed);
  const error =
    touched && !isValid
      ? trimmed.length === 0
        ? "Enter a name for the mapping."
        : "That name contains characters that aren't allowed."
      : null;

  const submit = (): void => {
    setTouched(true);
    if (isValid) onCreate(trimmed);
  };

  return (
    <DexterModal
      isOpen={isOpen}
      title="New Mapping"
      confirmLabel="Create"
      confirmDisabled={!isValid}
      onConfirm={submit}
      onCancel={onCancel}
    >
      {/* A form so pressing Enter in the field creates the mapping. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <FormControl
          label="Mapping name"
          error={error}
          overrides={{
            Label: {
              style: {
                color: colors.defaultTextColor,
                fontFamily: fonts.primary,
                fontSize: fontSizes.medium,
                fontWeight: 500,
              },
            },
          }}
        >
          <Input
            id="new-mapping-name-input"
            value={name}
            autoFocus
            error={Boolean(error)}
            onChange={(event) => setName(event.currentTarget.value)}
            onBlur={() => setTouched(true)}
            overrides={{
              Input: {
                style: {
                  fontFamily: fonts.primary,
                  fontSize: fontSizes.medium,
                },
              },
            }}
          />
        </FormControl>
      </form>
    </DexterModal>
  );
};

export default NewMappingModal;
